/**
 * Main App Component — Root of the React application.
 *
 * Sets up routing, auth context, and layout for the footage organization
 * and rough-cut assembly platform.
 */

import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

// Layout
import Layout from './components/Layout/Layout'

// Pages
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import HomePage from './pages/HomePage'
import ProjectsPage from './pages/ProjectsPage'
import ProjectDashboardPage from './pages/ProjectDashboardPage'
import IngestPage from './pages/IngestPage'
import ClipsPage from './pages/ClipsPage'
import InspectPage from './pages/InspectPage'
import AssembliesPage from './pages/AssembliesPage'
import AssemblyEditorPage from './pages/AssemblyEditorPage'
import AssemblyReviewPage from './pages/AssemblyReviewPage'
import ProcessingPage from './pages/ProcessingPage'
import CreateAiDraftPage from './pages/CreateAiDraftPage'

import AnalysisPage from './pages/AnalysisPage'
import SystemPage from './pages/SystemPage'
import MonitoringPage from './pages/MonitoringPage'

// Context providers
import { EmailProvider } from './contexts/EmailContext'
import { AuthProvider } from './contexts/AuthContext'
import { FileManagerProvider } from './contexts/FileManagerContext'

// Utilities
import { installGlobalStorageErrorHandler, monitorStorage } from './utils/storageErrorHandler'
import ErrorBoundary from './components/ErrorBoundary'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'

function App() {
  useEffect(() => {
    installGlobalStorageErrorHandler()
    monitorStorage(80).catch(() => {})
    const interval = setInterval(() => { monitorStorage(80).catch(() => {}) }, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  return (
    // Outermost, so a throw inside the providers is caught too. Without this
    // any render error anywhere in the app unmounts the whole tree and leaves
    // a blank page with no way back.
    <ErrorBoundary>
      <AuthProvider>
        <EmailProvider>
          <AppRoutes />
        </EmailProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

/** Route definitions — login/register are standalone; everything else inside Layout. */
function AppRoutes() {
  const location = useLocation()
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register'

  if (isAuthPage) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Routes>
    )
  }

  return (
    <FileManagerProvider>
    <Layout>
      <Routes>
        {/* Home */}
        <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />

        {/* Projects */}
        <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
        <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDashboardPage /></ProtectedRoute>} />
        <Route path="/projects/:projectId/ingest" element={<ProtectedRoute><IngestPage /></ProtectedRoute>} />

        {/* Asset views */}
        <Route path="/collections" element={<ProtectedRoute><Navigate to="/collections/clips" replace /></ProtectedRoute>} />
        <Route path="/collections/clips" element={<ProtectedRoute><ClipsPage /></ProtectedRoute>} />
        <Route path="/collections/buckets" element={<ProtectedRoute><ClipsPage /></ProtectedRoute>} />
        <Route path="/collections/clusters" element={<ProtectedRoute><ClipsPage /></ProtectedRoute>} />
        <Route path="/collections/analysis" element={<ProtectedRoute><ClipsPage /></ProtectedRoute>} />

        {/* Analysis */}
        <Route path="/analysis" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />

        {/* Assemblies */}
        <Route path="/create-ai-draft" element={<ProtectedRoute><CreateAiDraftPage /></ProtectedRoute>} />
        <Route path="/assemblies" element={<ProtectedRoute><AssembliesPage /></ProtectedRoute>} />
        <Route path="/assemblies/:assemblyId" element={<ProtectedRoute><AssemblyReviewPage /></ProtectedRoute>} />
        <Route path="/assemblies/:assemblyId/legacy" element={<ProtectedRoute><AssemblyEditorPage /></ProtectedRoute>} />
        <Route path="/assemblies/:assemblyId/processing" element={<ProtectedRoute><ProcessingPage /></ProtectedRoute>} />

        {/* Admin */}
        <Route path="/system" element={<ProtectedRoute><SystemPage /></ProtectedRoute>} />
        <Route path="/inspect" element={<AdminRoute><InspectPage /></AdminRoute>} />
        <Route path="/monitoring" element={<AdminRoute><MonitoringPage /></AdminRoute>} />
      </Routes>
    </Layout>
    </FileManagerProvider>
  )
}

export default App
